///
/// EarthBrowser Admin Dashboard - Feature Flags Section
///
/// Created: 2025-11-27
/// Copyright (c) 2025 Geodata Labs
///

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog"
import { Button } from "@/components/ui/button"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { Switch } from "@/components/ui/switch"
import {
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableHeader,
	TableRow,
} from "@/components/ui/table"
import { Textarea } from "@/components/ui/textarea"
import { toast } from "@/components/ui/use-toast"
import {
	useCreateFeatureFlagMutation,
	useDeleteFeatureFlagMutation,
	useGetFeatureFlagsQuery,
	useToggleFeatureFlagMutation,
	type FeatureFlag,
	type FeatureFlagCategory,
} from "@/redux/api/featureFlagsApi"
import type {
	FeatureFlagCreateRequest,
	FeatureFlagEnvironment,
	FeatureTargeting,
} from "@shared-types"
import {
	Beaker,
	Flag,
	Layers,
	Monitor,
	Plus,
	Settings,
	Trash2,
	Zap
} from "lucide-react"
import { useCallback, useState } from "react"

const categoryConfig: Record<
  FeatureFlagCategory,
  { label: string; bgColor: string; textColor: string; borderColor: string; icon: React.ReactNode }
> = {
  core: {
    label: "Core",
    bgColor: "bg-blue-500/10",
    textColor: "text-blue-600 dark:text-blue-400",
    borderColor: "border-blue-500/30",
    icon: <Settings className="h-3 w-3" />,
  },
  experimental: {
    label: "Experimental",
    bgColor: "bg-purple-500/10",
    textColor: "text-purple-600 dark:text-purple-400",
    borderColor: "border-purple-500/30",
    icon: <Beaker className="h-3 w-3" />,
  },
  ui: {
    label: "UI",
    bgColor: "bg-cyan-500/10",
    textColor: "text-cyan-600 dark:text-cyan-400",
    borderColor: "border-cyan-500/30",
    icon: <Monitor className="h-3 w-3" />,
  },
  data: {
    label: "Data",
    bgColor: "bg-amber-500/10",
    textColor: "text-amber-600 dark:text-amber-400",
    borderColor: "border-amber-500/30",
    icon: <Layers className="h-3 w-3" />,
  },
  performance: {
    label: "Performance",
    bgColor: "bg-emerald-500/10",
    textColor: "text-emerald-600 dark:text-emerald-400",
    borderColor: "border-emerald-500/30",
    icon: <Zap className="h-3 w-3" />,
  },
  general: {
    label: "General",
    bgColor: "bg-slate-500/10",
    textColor: "text-slate-600 dark:text-slate-400",
    borderColor: "border-slate-500/30",
    icon: <Flag className="h-3 w-3" />,
  },
}

function CategoryBadge({ category }: { category: FeatureFlagCategory }) {
  const config = categoryConfig[category] || categoryConfig.general
  return (
    <div className={`px-2.5 py-1 rounded-full ${config.bgColor} border ${config.borderColor} flex items-center gap-1.5 transition-all duration-300`}>
      <span className={config.textColor}>{config.icon}</span>
      <span className={`text-xs font-semibold ${config.textColor}`}>{config.label}</span>
    </div>
  )
}

function TargetingBadges({ targeting }: { targeting: FeatureTargeting }) {
  if (!targeting || Object.keys(targeting).length === 0) {
    return <span className="text-xs text-muted-foreground">Everyone</span>
  }

  return (
    <div className="flex gap-1.5 flex-wrap">
      {targeting.roles?.map((r) => (
        <div key={r} className="px-2 py-0.5 rounded-full bg-blue-500/10 border border-blue-500/20">
          <span className="text-xs font-medium text-blue-600 dark:text-blue-400">{r}</span>
        </div>
      ))}
      {targeting.percentage !== undefined && (
        <div className="px-2 py-0.5 rounded-full bg-cyan-500/10 border border-cyan-500/20">
          <span className="text-xs font-medium text-cyan-600 dark:text-cyan-400">{targeting.percentage}%</span>
        </div>
      )}
    </div>
  )
}

function EnvironmentBadge({ environment }: { environment: string }) {
  const config: Record<string, { bg: string; text: string; border: string }> = {
    all: { bg: "bg-slate-500/10", text: "text-slate-600 dark:text-slate-400", border: "border-slate-500/20" },
    production: { bg: "bg-red-500/10", text: "text-red-600 dark:text-red-400", border: "border-red-500/20" },
    development: { bg: "bg-emerald-500/10", text: "text-emerald-600 dark:text-emerald-400", border: "border-emerald-500/20" },
  }
  const c = config[environment] || config.all
  return (
    <div className={`px-2 py-0.5 rounded-full ${c.bg} border ${c.border} transition-all duration-300`}>
      <span className={`text-xs font-medium ${c.text}`}>{environment}</span>
    </div>
  )
}

function CreateFlagDialog({
  open,
  onOpenChange,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const [createFlag, { isLoading }] = useCreateFeatureFlagMutation()
  const [formData, setFormData] = useState<FeatureFlagCreateRequest>({
    key: "",
    name: "",
    description: "",
    enabled: false,
    category: "general",
    environment: "all",
    targeting: {},
  })
  const [targetingRoles, setTargetingRoles] = useState("")
  const [targetingPercentage, setTargetingPercentage] = useState("")

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const targeting: FeatureTargeting = {}
    if (targetingRoles.trim()) {
      targeting.roles = targetingRoles.split(",").map((r) => r.trim())
    }
    if (targetingPercentage && !isNaN(parseInt(targetingPercentage))) {
      targeting.percentage = Math.max(0, Math.min(100, parseInt(targetingPercentage)))
    }

    try {
      await createFlag({
        ...formData,
        targeting,
      }).unwrap()

      toast({
        title: "Feature flag created",
        description: `Flag "${formData.key}" has been created successfully.`,
      })

      setFormData({
        key: "",
        name: "",
        description: "",
        enabled: false,
        category: "general",
        environment: "all",
        targeting: {},
      })
      setTargetingRoles("")
      setTargetingPercentage("")
      onOpenChange(false)
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "data" in error &&
        error.data && typeof error.data === "object" && "message" in error.data
          ? String(error.data.message)
          : "Failed to create feature flag"
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px] rounded-3xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Flag className="h-5 w-5 text-primary" />
              Create Feature Flag
            </DialogTitle>
            <DialogDescription>
              Add a new feature flag to control feature availability.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="key">Key <span className="text-red-500">*</span></Label>
              <Input
                id="key"
                placeholder="my_new_feature"
                value={formData.key}
                onChange={(e) =>
                  setFormData({ ...formData, key: e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, "_") })
                }
                className="font-mono rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                required
              />
              <p className="text-xs text-muted-foreground">
                Unique identifier used in code (lowercase, underscores)
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="name">Name <span className="text-red-500">*</span></Label>
              <Input
                id="name"
                placeholder="My New Feature"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="description">Description</Label>
              <Textarea
                id="description"
                placeholder="What does this feature do?"
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                rows={2}
                className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Category</Label>
                <Select
                  value={formData.category}
                  onValueChange={(v) => setFormData({ ...formData, category: v as FeatureFlagCategory })}
                >
                  <SelectTrigger className="rounded-xl focus:ring-2 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    {Object.entries(categoryConfig).map(([key, config]) => (
                      <SelectItem key={key} value={key} className="rounded-lg">
                        <span className="flex items-center gap-2">
                          <span className={config.textColor}>{config.icon}</span>
                          {config.label}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label>Environment</Label>
                <Select
                  value={formData.environment}
                  onValueChange={(v) => setFormData({ ...formData, environment: v as FeatureFlagEnvironment })}
                >
                  <SelectTrigger className="rounded-xl focus:ring-2 focus:ring-blue-500">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="rounded-xl">
                    <SelectItem value="all" className="rounded-lg">All</SelectItem>
                    <SelectItem value="development" className="rounded-lg">Development</SelectItem>
                    <SelectItem value="production" className="rounded-lg">Production</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="roles">Target Roles</Label>
                <Input
                  id="roles"
                  placeholder="beta, admin, premium"
                  value={targetingRoles}
                  onChange={(e) => setTargetingRoles(e.target.value)}
                  className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                />
                <p className="text-xs text-muted-foreground">Comma-separated list of roles</p>
              </div>

              <div className="space-y-2">
                <Label htmlFor="percentage">Rollout %</Label>
                <Input
                  id="percentage"
                  type="number"
                  min={0}
                  max={100}
                  placeholder="100"
                  value={targetingPercentage}
                  onChange={(e) => setTargetingPercentage(e.target.value)}
                  className="rounded-xl transition-all duration-300 focus-visible:ring-2 focus-visible:ring-blue-500"
                />
                <p className="text-xs text-muted-foreground">Percentage of users (0–100)</p>
              </div>
            </div>

            <div className="flex items-center justify-between p-3 rounded-xl bg-muted">
              <div>
                <Label htmlFor="enabled">Enable immediately</Label>
                <p className="text-xs text-muted-foreground">Turn on this feature right away</p>
              </div>
              <Switch
                id="enabled"
                checked={formData.enabled}
                onCheckedChange={(checked) => setFormData({ ...formData, enabled: checked })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)} className="rounded-full transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-blue-500">
              Cancel
            </Button>
            <Button type="submit" disabled={isLoading} className="rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100">
              {isLoading ? "Creating..." : "Create Flag"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function FeatureFlagsSection() {
  const { data, isLoading, error } = useGetFeatureFlagsQuery()
  const [toggleFlag, { isLoading: isToggling }] = useToggleFeatureFlagMutation()
  const [deleteFlag, { isLoading: isDeleting }] = useDeleteFeatureFlagMutation()

  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [selectedFlag, setSelectedFlag] = useState<FeatureFlag | null>(null)
  const [categoryFilter, setCategoryFilter] = useState<string>("all")

  const handleToggle = useCallback(
    async (flag: FeatureFlag) => {
      try {
        await toggleFlag({
          id: flag.id,
          body: { enabled: !flag.enabled },
        }).unwrap()

        toast({
          title: flag.enabled ? "Feature disabled" : "Feature enabled",
          description: `"${flag.name}" has been ${flag.enabled ? "disabled" : "enabled"}.`,
        })
      } catch (error: unknown) {
        const errorMessage =
          error && typeof error === "object" && "data" in error &&
          error.data && typeof error.data === "object" && "message" in error.data
            ? String(error.data.message)
            : "Failed to toggle feature flag"
        toast({
          title: "Error",
          description: errorMessage,
          variant: "destructive",
        })
      }
    },
    [toggleFlag]
  )

  const handleDelete = useCallback(async () => {
    if (!selectedFlag) return

    try {
      await deleteFlag(selectedFlag.id).unwrap()
      toast({
        title: "Feature flag deleted",
        description: `"${selectedFlag.name}" has been deleted.`,
      })
      setDeleteDialogOpen(false)
      setSelectedFlag(null)
    } catch (error: unknown) {
      const errorMessage =
        error && typeof error === "object" && "data" in error &&
        error.data && typeof error.data === "object" && "message" in error.data
          ? String(error.data.message)
          : "Failed to delete feature flag"
      toast({
        title: "Error",
        description: errorMessage,
        variant: "destructive",
      })
    }
  }, [deleteFlag, selectedFlag])

  const openDelete = (flag: FeatureFlag) => {
    setSelectedFlag(flag)
    setDeleteDialogOpen(true)
  }

  const filteredFlags =
    categoryFilter === "all"
      ? data?.flags
      : data?.flags.filter((f) => f.category === categoryFilter)

  if (error) {
    return (
      <div className="rounded-xl border border-red-500/70 bg-red-500/10 p-6 text-center">
        <p className="font-semibold text-red-600 dark:text-red-400">Error loading feature flags</p>
        <p className="text-sm mt-2 text-red-500">Please check your permissions and try again.</p>
      </div>
    )
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap gap-2 items-center">
          {data?.categories &&
            Object.entries(data.categories).map(([cat, count]) =>
              count > 0 ? (
                <button
                  key={cat}
                  onClick={() => setCategoryFilter(categoryFilter === cat ? "all" : cat)}
                  className={`px-3 py-1.5 rounded-full transition-all flex items-center gap-1.5 ${
                    categoryConfig[cat as FeatureFlagCategory]?.bgColor || ""
                  } border ${
                    categoryConfig[cat as FeatureFlagCategory]?.borderColor || ""
                  } ${
                    categoryFilter === cat ? "ring-2 ring-primary ring-offset-2 ring-offset-background" : ""
                  } hover:scale-105`}
                >
                  <span className={categoryConfig[cat as FeatureFlagCategory]?.textColor}>
                    {categoryConfig[cat as FeatureFlagCategory]?.icon}
                  </span>
                  <span className={`text-xs font-semibold ${categoryConfig[cat as FeatureFlagCategory]?.textColor}`}>
                    {categoryConfig[cat as FeatureFlagCategory]?.label}: {count}
                  </span>
                </button>
              ) : null
            )}
          {categoryFilter !== "all" && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setCategoryFilter("all")}
              className="text-xs h-7"
            >
              Clear filter
            </Button>
          )}
        </div>
        <Button onClick={() => setCreateDialogOpen(true)} className="rounded-full transition-all duration-300 hover:scale-105 hover:shadow-xl bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 focus-visible:ring-2 focus-visible:ring-blue-500">
          <Plus className="h-4 w-4 mr-2" />
          New Flag
        </Button>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : filteredFlags?.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border bg-cyan-500/5 p-8 flex flex-col items-center justify-center text-center">
          <div className="p-3 rounded-full bg-cyan-500/10 mb-4">
            <Flag className="h-8 w-8 text-cyan-500" />
          </div>
          <h3 className="text-base font-semibold mb-1">No feature flags yet</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Create your first feature flag to get started.
          </p>
          <Button onClick={() => setCreateDialogOpen(true)} variant="outline" className="rounded-full transition-all duration-300 hover:scale-105 border-cyan-500/50 hover:bg-cyan-500/10 focus-visible:ring-2 focus-visible:ring-cyan-500">
            <Plus className="h-4 w-4 mr-2" />
            Create Flag
          </Button>
        </div>
      ) : (
        <div className="rounded-xl border border-border overflow-hidden transition-all duration-300">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Flag</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Targeting</TableHead>
                <TableHead>Environment</TableHead>
                <TableHead className="text-center">Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredFlags?.map((flag) => (
                <TableRow key={flag.id}>
                  <TableCell>
                    <div className="flex flex-col">
                      <code className="font-mono text-sm font-medium text-cyan-500">{flag.key}</code>
                      <span className="text-xs text-muted-foreground mt-0.5">{flag.name}</span>
                    </div>
                  </TableCell>
                  <TableCell>
                    <CategoryBadge category={flag.category} />
                  </TableCell>
                  <TableCell>
                    <TargetingBadges targeting={flag.targeting} />
                  </TableCell>
                  <TableCell>
                    <EnvironmentBadge environment={flag.environment} />
                  </TableCell>
                  <TableCell className="text-center">
                    {flag.enabled ? (
                      <div className="inline-flex px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 items-center gap-1.5 transition-all duration-300">
                        <div className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                        <span className="text-xs font-semibold text-emerald-600 dark:text-emerald-400">On</span>
                      </div>
                    ) : (
                      <div className="inline-flex px-3 py-1 rounded-full bg-slate-500/20 border border-slate-500/30 transition-all duration-300">
                        <span className="text-xs font-semibold text-slate-600 dark:text-slate-400">Off</span>
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end items-center gap-2">
                      <Switch
                        checked={flag.enabled}
                        onCheckedChange={() => handleToggle(flag)}
                        disabled={isToggling}
                        aria-label={`${flag.enabled ? "Disable" : "Enable"} ${flag.name}`}
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openDelete(flag)}
                        disabled={flag.isPermanent || isDeleting}
                        title={flag.isPermanent ? "Permanent flag" : "Delete flag"}
                        className="hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <CreateFlagDialog open={createDialogOpen} onOpenChange={setCreateDialogOpen} />

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent className="rounded-3xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Trash2 className="h-5 w-5 text-red-500" />
              Delete Feature Flag
            </AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete <strong>{selectedFlag?.name}</strong>?
              This action cannot be undone. Any code referencing{" "}
              <code className="font-mono text-cyan-500">{selectedFlag?.key}</code> will default to
              disabled.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-full">Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="rounded-full bg-red-600 hover:bg-red-700 transition-all duration-300 hover:scale-105 focus-visible:ring-2 focus-visible:ring-red-500 disabled:hover:scale-100"
            >
              {isDeleting ? "Deleting..." : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}

